import { useId } from 'react';

/**
 * Logo Kevins Clínicas — usado em Header, Sidebar, LoginPage, PatientPortal.
 *
 * Marca: monograma "K" itálico (Kevins) em dois tons — barras sólidas,
 * braço inferior em cyan (movimento + toque de estética). Neutro de
 * especialidade: a plataforma atende várias áreas da saúde (odontologia,
 * estética, fisioterapia...), então o antigo dente limitava a marca.
 *
 * Variantes:
 *   - `mark`: só o símbolo (quadrado), para favicons/menus colapsados
 *   - `lockup`: símbolo + wordmark horizontal (default)
 *
 * Tamanhos: passe `size` em px; o componente respeita aspect-ratio. O
 * SVG é inline (não <img>) pra herdar cores e escalar sem perda.
 *
 * `inverted`: wordmark em branco (para uso sobre fundos teal/escuros).
 * `animated`: um brilho diagonal percorre o símbolo + leve "respiro" do
 * K. Opt-in pra não distrair onde o logo é só chrome (sidebar/header).
 * Respeita `prefers-reduced-motion`.
 */

interface Props {
  variant?: 'mark' | 'lockup';
  size?: number;
  className?: string;
  /**
   * Quando true, renderiza o wordmark em branco (para uso sobre
   * fundos teal/escuros). Default false.
   */
  inverted?: boolean;
  /** Liga a animação de brilho + respiro. Default false. */
  animated?: boolean;
}

export function Logo({
  variant = 'lockup',
  size = 32,
  className = '',
  inverted = false,
  animated = false,
}: Props) {
  // IDs únicos por instância — evita colisão de <defs> quando há vários
  // logos na mesma página (gradients/clips compartilhariam id senão).
  const uid = useId().replace(/:/g, '');
  const bg = `bg-${uid}`;
  const clip = `clip-${uid}`;

  const wordmarkFill = inverted ? '#ffffff' : '#0f172a';
  const wordmarkAccent = inverted ? '#5eead4' : '#0e7490';

  const isLockup = variant === 'lockup';
  // viewBox mais largo no lockup pra acomodar "Kevins Clínicas" sem cortar.
  const width = isLockup ? (size * 300) / 64 : size;
  const viewBox = isLockup ? '0 0 300 64' : '0 0 64 64';

  return (
    <svg
      viewBox={viewBox}
      width={width}
      height={size}
      className={className}
      role="img"
      aria-label="Kevins Clínicas"
    >
      <title>Kevins Clínicas</title>

      <defs>
        {/* Fundo do símbolo: gradiente teal→cyan diagonal. */}
        <linearGradient id={bg} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0d9488" />
          <stop offset="55%" stopColor="#0e7490" />
          <stop offset="100%" stopColor="#155e75" />
        </linearGradient>
        <clipPath id={clip}>
          <rect width="64" height="64" rx="14" />
        </clipPath>
      </defs>

      {animated && (
        <style>{`
          @media (prefers-reduced-motion: no-preference) {
            .df-k-${uid} {
              transform-box: fill-box;
              transform-origin: center;
              animation: df-k-breathe 3.2s ease-in-out infinite;
            }
            .df-shine-${uid} {
              animation: df-shine-sweep 3.2s ease-in-out infinite;
            }
          }
          @keyframes df-k-breathe {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.03); }
          }
          @keyframes df-shine-sweep {
            0% { transform: translateX(-46px) skewX(-18deg); opacity: 0; }
            18% { opacity: .55; }
            45%, 100% { transform: translateX(64px) skewX(-18deg); opacity: 0; }
          }
        `}</style>
      )}

      {/* ── Símbolo: tile + monograma K itálico ─────────────────────── */}
      <rect width="64" height="64" rx="14" fill={`url(#${bg})`} />

      <g className={animated ? `df-k-${uid}` : undefined}>
        <g transform="translate(7 0) skewX(-9.5)">
          {/* haste */}
          <rect x="17" y="14" width="9" height="36" rx="4.5" fill="#ffffff" />
          {/* braço superior */}
          <rect
            x="22"
            y="19"
            width="28"
            height="9"
            rx="4.5"
            fill="#ffffff"
            transform="rotate(-37.68 36 23.5)"
          />
          {/* braço inferior — accent cyan (movimento + estética) */}
          <rect
            x="21.5"
            y="36.5"
            width="31"
            height="9"
            rx="4.5"
            fill="#5eead4"
            transform="rotate(40 36.5 41)"
          />
        </g>
      </g>

      {/* Brilho diagonal que percorre o símbolo (só quando animado) */}
      {animated && (
        <g clipPath={`url(#${clip})`}>
          <rect
            className={`df-shine-${uid}`}
            x="-20"
            y="-10"
            width="18"
            height="84"
            fill="#ffffff"
            opacity="0"
          />
        </g>
      )}

      {/* ── Wordmark (só no lockup) ─────────────────────────────────── */}
      {isLockup && (
        <text
          x="78"
          y="40"
          fontFamily="Inter, system-ui, -apple-system, sans-serif"
          fontSize="22"
          fontWeight="600"
          fill={wordmarkFill}
          letterSpacing="-0.5"
        >
          Kevins
          <tspan fill={wordmarkAccent}> Clínicas</tspan>
        </text>
      )}
    </svg>
  );
}
